import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Fixed typing pace (characters/second), used ONLY when the line has no audio — the
// voice failed, was abandoned, or never started. A comfortable reading speed.
export const TYPING_CPS = 40;
// How long a voiced line waits for its audio to start before the text starts
// typing anyway at the fixed pace (paused time doesn't count) — so a late voice
// never leaves the caption blank. Short: with the next line's audio prefetched, a
// voiced line normally starts within a few hundred ms; if the voice does turn up
// after this, the reveal snaps back into step with it (see below).
export const AUDIO_WAIT_MS = 1500;

// Typewriter reveal for the spoken text, DRIVEN BY THE AUDIO ELEMENT.
//
// OpenAI TTS has no word timings, so the reveal is pegged to playback position:
//   • nothing is revealed until the audio actually starts (`startedRef`, set on the
//     element's 'playing' event) — not when the text arrives;
//   • at 'playing' the reveal RESETS to the audio's position (≈0) — anything the
//     fallback had typed while waiting is discarded, so the words line up with
//     the voice from the first one;
//   • from then on, revealed = round(length × currentTime / duration)
//     (`progressRef`, which useTtsPlayback only writes once `duration` is a real,
//     finite number — so a NaN/Infinity duration before metadata reveals nothing
//     rather than everything). It only ever moves forward, pauses with the audio,
//     and reaches the full text on 'ended' (progress 1);
//   • the fixed TYPING_CPS pace runs only when there's no audio: `noAudioRef` says
//     the voice failed or was abandoned, there's no `startedRef` to follow at all,
//     or AUDIO_WAIT_MS passed without playback starting.
//
// A new `runKey` restarts the reveal from nothing (a new utterance, or a Replay —
// which re-plays the audio, so the reveal re-syncs to it). A null/undefined runKey
// means "nothing being spoken" → the text is shown in full. `complete()` jumps
// straight to the full text and ends the reveal loop.
export default function useTypewriter({
    text,
    runKey,
    paused = false,
    startedRef,
    progressRef,
    noAudioRef,
    audioWaitMs = AUDIO_WAIT_MS,
}) {
    const full = text || "";
    const total = full.length;
    const animate = runKey !== null && runKey !== undefined;
    const [count, setCount] = useState(animate ? 0 : total);
    const countRef = useRef(count);
    const completedRef = useRef(false); // complete() pressed → stays full
    const rafRef = useRef(null); // the pending reveal frame, so complete() can cancel it
    const pausedRef = useRef(paused);
    pausedRef.current = paused;

    const set = (n) => {
        countRef.current = n;
        setCount(n);
    };

    // Layout effect: the reset to 0 lands before paint, so a new utterance never
    // flashes in full for a frame.
    useLayoutEffect(() => {
        if (!animate || !total) {
            set(total);
            return undefined;
        }
        set(0);
        completedRef.current = false;
        let shown = 0; // characters revealed (fractional while typing at the fixed pace)
        let waited = 0; // unpaused ms spent waiting for the audio to start
        let synced = false; // the audio has taken over
        let last = null;
        const tick = (ts) => {
            const dt = last == null ? 0 : Math.max(0, ts - last);
            last = ts;
            if (startedRef && startedRef.current) {
                // Audio-driven. Monotonic: a seek back / rewind never un-types.
                const prog = Math.min(1, Math.max(0, Number((progressRef && progressRef.current) || 0) || 0));
                const atAudio = Math.round(total * prog);
                // First frame of playback: snap to the voice, dropping any head
                // the fallback typed. After that, forward only.
                shown = synced ? Math.max(shown, atAudio) : atAudio;
                synced = true;
            } else if (!pausedRef.current) {
                waited += dt;
                const noAudio = !startedRef || (noAudioRef && noAudioRef.current) || waited >= audioWaitMs;
                if (noAudio) shown += (dt * TYPING_CPS) / 1000;
            }
            if (completedRef.current) return; // complete()d meanwhile
            const n = Math.min(total, Math.floor(shown));
            if (n !== countRef.current) set(n);
            // Keep watching while there's text left — or while a fallback-typed line
            // may still get its voice (so it can snap into sync when it starts).
            const audioMayStart = !synced && startedRef && !(noAudioRef && noAudioRef.current);
            rafRef.current = n < total || audioMayStart ? requestAnimationFrame(tick) : null;
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runKey, full, animate]);

    // Full text now, and stop the reveal loop outright — nothing keeps ticking
    // behind a completed caption (Skip intro stops the audio, which would otherwise
    // drop the reveal onto the fixed-pace fallback and type the rest out).
    const complete = useCallback(() => {
        completedRef.current = true;
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (countRef.current < total) set(total);
    }, [total]);

    return { shown: full.slice(0, count), count, done: count >= total, complete };
}
